import { AllCategoryMap, CatalogCategory, L3SubGroup } from '@/shared/types/catalog';
import './index.less';
import { useEffect, useMemo, useState } from 'react';
import { SourcingHomeTab, ViewType } from '@/shared/types/sourcing-marketplace';
import cN from 'classnames';
import { CategoryConfig, VerticalName } from '@/shared/types/category';
import { Checkbox } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useQueryParams } from '@/shared/hooks';
import { ParamKey } from '@/shared/types/url-params';
import { getSubcategories } from '@/shared/utils/sourcing-marketplace-util';
import { cloneDeep, isEmpty, map, sumBy, toPairs, values } from 'lodash-es';
import { useRouter } from 'next/router';
import { useParams } from 'next/navigation';
import { customChain } from '@/shared/utils/chain';
import { useHistoryList } from '@/lib/context/HistoryListProvider';
import { HOMEPAGE_TAB_TO_TEXT } from '@/shared/constants/sourcing-marketplace';

interface SourcingCategoryFilterProps {
  L2CategoryConfig: CategoryConfig;
  L2Vertical: VerticalName;
  allL1ToL2ToL3Map: AllCategoryMap;
  ignoreL3CategoryIds?: number[];
  L3IdToCountMap?: { [key: string]: number };
  viewType: ViewType;
  allEntryPointCategoryConfig: {
    [key: string]: CategoryConfig[];
  };
}

const MAX_CATEGORY_OPTIONS_TO_SHOW = 4;

const getCategoryTabName = (tabKey: SourcingHomeTab) => {
  switch (tabKey) {
    case SourcingHomeTab.PRODUCTS:
      return 'Product factories';
    case SourcingHomeTab.PACKAGING:
      return 'Packaging factories';
    case SourcingHomeTab.BUSINESS_AND_CREATIVE_SERVICES:
      return 'Freelancers & agencies';
    case SourcingHomeTab.WEBSITE_DEVELOPMENT:
      return 'Website developers';
    case SourcingHomeTab.ALL:
    default:
      return '';
  }
};

const getVerticalName = (vertical: string, verticalName: string) => {
  if (vertical === VerticalName.PRODUCTS) {
    return SourcingHomeTab.PRODUCTS;
  } else if (vertical === VerticalName.PACKAGING) {
    return SourcingHomeTab.PACKAGING;
  } else if (vertical === VerticalName.SERVICES) {
    if (verticalName.startsWith('web')) {
      return SourcingHomeTab.WEBSITE_DEVELOPMENT;
    } else {
      return SourcingHomeTab.BUSINESS_AND_CREATIVE_SERVICES;
    }
  }
};

const SourcingCategoryFilter = ({
  L2CategoryConfig,
  L2Vertical,
  allL1ToL2ToL3Map,
  ignoreL3CategoryIds,
  L3IdToCountMap,
  viewType,
  allEntryPointCategoryConfig,
}: SourcingCategoryFilterProps) => {
  const params = useParams();
  const router = useRouter();
  const { queryParams, setQueryParams } = useQueryParams<{}>();
  const [ignoreL3Ids, setIgnoreL3Ids] = useState<number[]>(ignoreL3CategoryIds);
  const [showVerticalAll, setShowVerticalAll] = useState<{ [key: string]: boolean }>({});
  const searchKey = queryParams.get(ParamKey.SEARCH);
  const L2Handle = params?.category;
  const L2VertivalInUrl = queryParams.get(ParamKey.L2_VERTICAL);
  const L2VerticalNameInUrl = queryParams.get(ParamKey.L2_VERTICAL_NAME);
  const { sourcingBreadcrumbs, setSourcingBreadcrumbs } = useHistoryList();

  const allCategoryMapBySourcingTab = useMemo(() => {
    if (allL1ToL2ToL3Map) {
      return {
        [SourcingHomeTab.PRODUCTS]: customChain(allL1ToL2ToL3Map)
          .toPairs()
          .filter(([k, v]) => !['Packaging', 'Services'].includes(k))
          .reduce((obj, [key, value]) => ({ ...obj, ...value }), {})
          .value(),
        [SourcingHomeTab.PACKAGING]: allL1ToL2ToL3Map['Packaging'],
        [SourcingHomeTab.BUSINESS_AND_CREATIVE_SERVICES]: customChain(allL1ToL2ToL3Map['Services'])
          .toPairs()
          .filter(([k, v]) => !k.startsWith('Web'))
          .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {})
          .value(),
        [SourcingHomeTab.WEBSITE_DEVELOPMENT]: customChain(allL1ToL2ToL3Map['Services'])
          .toPairs()
          .filter(([k, v]) => k.startsWith('Web'))
          .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {})
          .value(),
      };
    }

    return {};
  }, [allL1ToL2ToL3Map]);

  const backToAllL2Categories = () => {
    setSourcingBreadcrumbs([
      {
        type: 'L1',
        label:
          L2Vertical === VerticalName.SERVICES
            ? HOMEPAGE_TAB_TO_TEXT[SourcingHomeTab.BUSINESS_AND_CREATIVE_SERVICES]
            : L2Vertical === VerticalName.PACKAGING
              ? HOMEPAGE_TAB_TO_TEXT[SourcingHomeTab.PACKAGING]
              : HOMEPAGE_TAB_TO_TEXT[SourcingHomeTab.PRODUCTS],
        L1Key:
          L2Vertical === VerticalName.SERVICES
            ? SourcingHomeTab.BUSINESS_AND_CREATIVE_SERVICES
            : L2Vertical === VerticalName.PACKAGING
              ? SourcingHomeTab.PACKAGING
              : SourcingHomeTab.PRODUCTS,
      },
    ]);

    router.push({
      pathname: `/sourcing/browse/all/${viewType === ViewType.CATALOG_PRODUCTS ? 'products' : 'suppliers'}`,
      query: {
        [ParamKey.SEARCH]: searchKey,
        [ParamKey.L2_VERTICAL]: L2Vertical,
        [ParamKey.L2_VERTICAL_NAME]: getVerticalName(L2Vertical, L2CategoryConfig?.key),
      },
    });
  };

  const handleL3IsChecked = (L3Id: number) => {
    return !ignoreL3Ids.includes(L3Id) || isEmpty(ignoreL3Ids);
  };

  const clickL2Category = (L1Name: string, L2CategoryName: string) => {
    const allL2Categories = L2VertivalInUrl
      ? allEntryPointCategoryConfig[L2VerticalNameInUrl]
      : values(allEntryPointCategoryConfig).flat();

    const L2Category = values(allL2Categories)
      .flat()
      .find((category) => category.group === L2CategoryName);

    setSourcingBreadcrumbs([
      {
        type: 'L1',
        label: HOMEPAGE_TAB_TO_TEXT[L1Name],
        L1Key: L1Name,
      },
      {
        type: 'L2',
        label: L2Category?.group,
        L2Key: L2Category?.key,
      },
    ]);

    router.push({
      pathname: `/sourcing/browse/${L2Category?.key}/${viewType === ViewType.CATALOG_PRODUCTS ? 'products' : 'suppliers'}`,
    });
  };

  const clickL3Category = (L3Category) => {
    const L3CategoryId = L3Category?.id;
    const isChecked = handleL3IsChecked(L3CategoryId);
    let newIgnoreL3CategoryIds = [];

    // If ignoreL3CategoryIds is empty, it means all L3 categories are selected
    // now, if user click one, only keep this one as checked, others are unchecked
    if (isEmpty(ignoreL3Ids)) {
      const allL3Categories = allL1ToL2ToL3Map?.[L2CategoryConfig?.vertical]?.[L2CategoryConfig?.group];
      const allL3Ids: number[] = customChain(allL3Categories)
        .toPairs()
        .map(([key, category]) => category.id)
        .value();

      newIgnoreL3CategoryIds = allL3Ids.filter((id) => id !== L3CategoryId);
    } else {
      // If isChecked is true, it means the current value is true, want to set its new value to false => add it to ignore l3 ids
      // If isChecked is false, it means the current value is false, want to set its new value to true => remove it from ignore l3 ids
      if (isChecked) {
        newIgnoreL3CategoryIds = [...ignoreL3Ids, L3CategoryId];
      } else {
        newIgnoreL3CategoryIds = ignoreL3Ids.filter((id) => id !== L3CategoryId);
      }
    }
    // Set new value to state & url query params
    setIgnoreL3Ids(newIgnoreL3CategoryIds);
    setQueryParams({
      [ParamKey.IGNORE_L3_CATEGORY_IDS]:
        newIgnoreL3CategoryIds && newIgnoreL3CategoryIds?.length > 0 ? newIgnoreL3CategoryIds.join(',') : null,
    });
  };

  const getL3Counts = (L3Category: CatalogCategory) => {
    const subCategories = getSubcategories(
      allL1ToL2ToL3Map,
      L3Category.vertical,
      L3Category.group,
      L3Category.subgroup,
    );
    const subCategoryIds = map(subCategories, 'id');
    const count = sumBy(subCategoryIds, (id) => L3IdToCountMap?.[id]) || 0;

    return searchKey
      ? count
      : viewType === ViewType.SUPPLIERS
        ? (L3Category?.catalogCompanyCount ?? 0)
        : (L3Category?.catalogItemCount ?? 0);
  };

  const getL2Counts = (data: L3SubGroup) => {
    let count = 0;

    if (searchKey) {
      toPairs(data).forEach(([key, value]) => {
        count += L3IdToCountMap?.[value.id] ?? 0;
      });
    } else {
      toPairs(data).forEach(([key, value]) => {
        if (viewType === ViewType.SUPPLIERS) {
          count += value?.catalogCompanyCount ?? 0;
        } else {
          count += value?.catalogItemCount ?? 0;
        }
      });
    }

    return count;
  };

  const renderAllCategories = (tabKey) => {
    const categories = customChain(allCategoryMapBySourcingTab[tabKey])
      .toPairs()
      .map((x) => [...x, getL2Counts(x[1])])
      .filter((x) => !searchKey || x[2] > 0)
      .sortBy((x) => x[2])
      .reverse()
      .value();

    if (categories?.length === 0) return null;

    return (
      <div className="tab-category-group" key={tabKey}>
        <span className="title">{getCategoryTabName(tabKey)}</span>

        <div className="categories-box">
          {categories
            .slice(0, showVerticalAll[tabKey] ? categories.length : MAX_CATEGORY_OPTIONS_TO_SHOW)
            .map(([key, value, count = 0]) => {
              return (
                <span
                  key={`L2-${key}`}
                  onClick={() => {
                    clickL2Category(tabKey, key);
                  }}
                  className={cN('category-name', `item-count-${count}`)}
                >
                  {key}
                </span>
              );
            })}

          {!showVerticalAll[tabKey] && categories.length > MAX_CATEGORY_OPTIONS_TO_SHOW && (
            <span className="see-more" onClick={() => setShowVerticalAll((v) => ({ ...v, [tabKey]: true }))}>
              See more
            </span>
          )}
          {showVerticalAll[tabKey] && categories.length > MAX_CATEGORY_OPTIONS_TO_SHOW && (
            <span
              className="see-more"
              onClick={() => {
                const temp = cloneDeep(showVerticalAll);
                delete temp[tabKey];
                setShowVerticalAll(temp);
              }}
            >
              See less
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderL2Categories = () => {
    const L2Categories = customChain(allCategoryMapBySourcingTab[L2VerticalNameInUrl])
      .toPairs()
      .map((x) => [...x, getL2Counts(x[1])])
      .filter((x) => !searchKey || x[2] > 0)
      .sortBy((x) => x[2])
      .reverse()
      .value();

    if (L2Categories?.length === 0) return null;

    return (
      <div className="tab-category-group">
        <span className="title">{getCategoryTabName(L2VerticalNameInUrl as SourcingHomeTab)}</span>

        <div className="categories-box">
          {L2Categories.slice(
            0,
            showVerticalAll[L2VertivalInUrl] ? L2Categories.length : MAX_CATEGORY_OPTIONS_TO_SHOW,
          ).map(([key, value, count = 0]) => {
            return (
              <div
                key={`L2-${key}`}
                className="category-name"
                onClick={() => {
                  clickL2Category(L2VerticalNameInUrl, key);
                }}
              >
                {key}
                <RightOutlined />
              </div>
            );
          })}

          {!showVerticalAll[L2VertivalInUrl] && L2Categories.length > MAX_CATEGORY_OPTIONS_TO_SHOW && (
            <span className="see-more" onClick={() => setShowVerticalAll((v) => ({ ...v, [L2VertivalInUrl]: true }))}>
              See more
            </span>
          )}
          {showVerticalAll[L2VertivalInUrl] && (
            <span
              className="see-more"
              onClick={() => {
                const temp = cloneDeep(showVerticalAll);
                delete temp[L2VertivalInUrl];
                setShowVerticalAll(temp);
              }}
            >
              See less
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderL3Categories = () => {
    const allL3Categories = allL1ToL2ToL3Map?.[L2CategoryConfig?.vertical]?.[L2CategoryConfig?.group];

    const L3Categories = !isEmpty(allL3Categories)
      ? customChain(allL3Categories)
          .toPairs()
          .map(([key, value]) => value)
          .sortBy((x) => getL3Counts(x))
          .reverse()
          .value()
      : [];

    return (
      <div className="L3-categories-container">
        <div className="selected-group" onClick={backToAllL2Categories}>
          <LeftOutlined />
          <span className="title">{L2CategoryConfig?.title}</span>
        </div>
        <div className="options" style={{ marginLeft: '16px' }}>
          {L3Categories?.map((category) => {
            const searchResultsCount = getL3Counts(category);
            // If the count of this L3 category is 0, don't show it
            if (searchKey && searchResultsCount === 0) return null;

            return (
              <Checkbox
                key={category.id}
                value={category.id}
                className={cN('option')}
                checked={handleL3IsChecked(category?.id)}
                onClick={() => {
                  clickL3Category(category);
                }}
              >
                {category.subgroup}
              </Checkbox>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDifferentLevelCategory = () => {
    if (L2Handle === 'all' && L2VertivalInUrl) {
      return renderL2Categories();
    }

    if (L2CategoryConfig && L2CategoryConfig?.key !== SourcingHomeTab.ALL && L2Vertical) {
      return renderL3Categories();
    }

    return (
      <div className="category-container">
        {toPairs(allCategoryMapBySourcingTab).map(([key, value]) => {
          return <>{renderAllCategories(key)}</>;
        })}
      </div>
    );
  };

  return <div className="sourcing-category-filter-box">{renderDifferentLevelCategory()}</div>;
};

export default SourcingCategoryFilter;
